import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../composants/Button/Button';
import TableauDeBord from '../../../../composants/TableauDeBord/TableauDeBord';
import './MesCandidaturesTab.css';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CandidatureResumee {
    id: number;
    ref_avis: string;
    accord_cadre_nom: string;
    lot_technique_libelle: string;
    statut: 'brouillon' | 'soumise' | 'en_analyse' | 'acceptee' | 'refusee';
    created_at: string;
    /** "ac27" — prévu pour d'autres modules futurs */
    source_module: string;
}

export interface InvitationGroupementResumee {
    id: number;
    status: 'pending' | 'accepted' | 'refused';
    candidature: {
        id: number;
        lot_technique?: { code: string; nom: string };
        entite?: { nom: string };
    };
}

interface MesCandidaturesTabProps {
    candidatures: CandidatureResumee[];
    invitations: InvitationGroupementResumee[];
    loading: boolean;
    onRefresh: () => void;
}

// ── StatutBadge ───────────────────────────────────────────────────────────────

function StatutBadge({ statut }: { statut: CandidatureResumee['statut'] }) {
    const config: Record<string, { label: string; color: string }> = {
        brouillon: { label: 'Brouillon', color: '#6b7280' },
        soumise: { label: 'Soumise', color: '#f97316' },
        en_analyse: { label: 'En analyse', color: '#3b82f6' },
        acceptee: { label: 'Acceptée', color: '#22c55e' },
        refusee: { label: 'Refusée', color: '#ef4444' },
    };
    const { label, color } = config[statut] ?? config.brouillon;
    return (
        <span
            className="mes-candidatures-statut-badge"
            style={{ backgroundColor: color }}
        >
            {label}
        </span>
    );
}

// ── ModalInvitationsGroupement ────────────────────────────────────────────────

function ModalInvitationsGroupement({
    invitations,
    onClose,
    onHandled,
}: {
    invitations: InvitationGroupementResumee[];
    onClose: () => void;
    onHandled: () => void;
}) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');

    const handleAccept = (invId: number) => {
        fetch(`${apiUrl}/publications/ac27/groupement-invitations/${invId}/accept`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
        }).then(() => onHandled());
    };

    const handleRefuse = (invId: number) => {
        fetch(`${apiUrl}/publications/ac27/groupement-invitations/${invId}/refuse`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ raison: '' }),
        }).then(() => onHandled());
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Invitations groupement en attente ({invitations.length})</h2>
                    <button className="modal-close-btn" onClick={onClose} aria-label="Fermer">
                        ×
                    </button>
                </div>
                <div className="modal-body">
                    {invitations.map((inv) => (
                        <div key={inv.id} className="invitation-card">
                            <div className="invitation-info">
                                <p className="invitation-title">
                                    Invitation groupement —{' '}
                                    {inv.candidature.lot_technique
                                        ? `Lot ${inv.candidature.lot_technique.code} — ${inv.candidature.lot_technique.nom}`
                                        : `Candidature #${inv.candidature.id}`}
                                </p>
                                {inv.candidature.entite && (
                                    <p className="invitation-mandataire">
                                        Mandataire : <strong>{inv.candidature.entite.nom}</strong>
                                    </p>
                                )}
                            </div>
                            <div className="invitation-actions">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    value="Refuser"
                                    size="small"
                                    onClick={() => handleRefuse(inv.id)}
                                />
                                <Button
                                    type="button"
                                    variant="primary"
                                    value="Accepter"
                                    size="small"
                                    onClick={() => handleAccept(inv.id)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ── MesCandidaturesTab ────────────────────────────────────────────────────────

export default function MesCandidaturesTab({
    candidatures,
    invitations,
    loading,
    onRefresh,
}: MesCandidaturesTabProps) {
    const navigate = useNavigate();
    const [showGroupementModal, setShowGroupementModal] = useState(false);

    const invitationsPending = invitations.filter((i) => i.status === 'pending');

    // Ouvre la popup automatiquement si des invitations sont en attente
    useEffect(() => {
        if (invitationsPending.length > 0) {
            setShowGroupementModal(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invitationsPending.length]);

    const handleInvitationHandled = () => {
        onRefresh();
    };

    const columns = useMemo(() => [
        {
            field: 'ref_avis',
            header: 'Ref avis',
            renderCell: (value: string) => <div>{value || '—'}</div>,
        },
        {
            field: 'accord_cadre_nom',
            header: 'Accord-Cadre',
            renderCell: (value: string) => <div className="text-truncate" title={value}>{value}</div>,
        },
        {
            field: 'lot_technique_libelle',
            header: 'Lot technique',
            renderCell: (value: string) => <div className="text-truncate" title={value}>{value || '—'}</div>,
        },
        {
            field: 'statut',
            header: 'Statut',
            renderCell: (value: CandidatureResumee['statut']) => (
                <StatutBadge statut={value} />
            ),
        },
        {
            field: 'created_at',
            header: 'Créé le',
            renderCell: (value: string) => {
                const d = value ? new Date(value) : null;
                return <div>{d && !isNaN(d.getTime()) ? d.toLocaleDateString('fr-FR') : '—'}</div>;
            },
        },
        {
            field: 'id',
            header: 'Actions',
            renderCell: (_: number, row: CandidatureResumee) => (
                <Button
                    type="button"
                    variant={row.statut === 'brouillon' ? 'primary' : 'secondary'}
                    value={row.statut === 'brouillon' ? 'Continuer' : 'Voir'}
                    size="small"
                    onClick={() =>
                        navigate(`/publications/${row.source_module}/candidatures/${row.id}`)
                    }
                />
            ),
        },
    ], [navigate]);

    return (
        <div className="mes-candidatures-container">
            {/* Badge invitations (si popup fermée et pending restants) */}
            {invitationsPending.length > 0 && !showGroupementModal && (
                <button
                    className="invitations-badge"
                    onClick={() => setShowGroupementModal(true)}
                >
                    <span className="font-semibold">
                        {invitationsPending.length} invitation(s) groupement en attente
                    </span>
                    <span>→ Voir</span>
                </button>
            )}

            {/* Tableau candidatures via composant partagé */}
            {loading ? (
                <div className="mes-candidatures-loading">
                    <div className="spinner"></div>
                    <p>Chargement en cours…</p>
                </div>
            ) : candidatures.length === 0 ? (
                <div className="mes-candidatures-empty">
                    <p>Vous n'avez aucune candidature en cours.</p>
                    <p className="mes-candidatures-empty-hint">
                        Créez une candidature depuis le bouton sur la ligne concernée.
                    </p>
                </div>
            ) : (
                <TableauDeBord
                    data={candidatures}
                    columns={columns}
                    showActionsColumn={false}
                    isSelectable={false}
                    showActionBar={false}
                    selectedItems={[]}
                    onSelectionChange={() => { }}
                />
            )}

            {/* Popup groupement */}
            {showGroupementModal && invitationsPending.length > 0 && (
                <ModalInvitationsGroupement
                    invitations={invitationsPending}
                    onClose={() => setShowGroupementModal(false)}
                    onHandled={handleInvitationHandled}
                />
            )}
        </div>
    );
}
